import type { SeasonDeepContent } from "./types";

export const autumnDeepContent: SeasonDeepContent = {
  SOFT: {
    intro: `Soft Autumn sits at the muted, low-contrast end of the warm palette family — the bridge between Soft Summer and True Autumn. Undertone is warm but never obviously golden; think warmth diluted with grey rather than warmth turned up bright. Value runs light-to-medium, and chroma is the defining trait: every color in this soft autumn color palette is dusty, blended, and gently faded, like an early October afternoon rather than peak foliage. Where True Autumn goes rich and saturated, Soft Autumn stays quiet. Where Soft Summer goes cool, Soft Autumn tips warm. Hair, skin, and eyes tend to sit close in depth, giving an overall blended, understated look rather than sharp contrast. Bright, clear, or icy colors read as costume-like against this coloring; muted golds, sage, dusty coral, and warm taupe read as effortless. If you've been told you look "washed out" in both true reds and cool pastels, soft, blended earth tones are usually the fix.`,
    selfCheck: {
      skin: [
        "Warm undertone, but soft — not obviously golden or peachy",
        "Skin and hair sit close in depth, giving a blended rather than contrasted look",
        "Bright blush or bronzer looks heavy; sheer, muted warmth looks natural",
        "Tans easily but rarely goes deep bronze",
      ],
      hair: [
        "Muted brown, soft ash-warm blonde, or dirty blonde — never platinum or jet black",
        "Natural highlights look soft and low-contrast, not chunky or bright",
        "Warm colorist tones (like copper or golden) look best kept dialed down",
        "Grey tends to come in soft and blend rather than stark silver",
      ],
      eyes: [
        "Soft hazel, muted green, soft brown, or grey-blue — rarely a saturated color",
        "Eyes often have flecks of multiple soft tones rather than one solid color",
        "Bright eyeliner (cobalt, emerald) overpowers rather than enhances",
        "Warm, muted shadow shades (taupe, soft olive) make eyes look brighter",
      ],
      overall: [
        "Low contrast between skin, hair, and eyes — nothing 'jumps out'",
        "Bright, saturated clothing overwhelms your face; muted tones let your features lead",
        "Both soft gold and rose gold jewelry look fine; bright yellow gold can feel harsh",
        "You look healthiest in dusty, blended color, not clear or icy shades",
      ],
    },
    avoidDetails: [
      {
        name: "Bright White",
        hex: "#FFFFFF",
        reason: "Stark white is too cold and high-contrast for Soft Autumn's blended, muted coloring.",
        swapFor: "Wheat",
        swapHex: "#F5DEB3",
      },
      {
        name: "Jet Black",
        hex: "#000000",
        reason: "Pure black creates harsh contrast that overwhelms Soft Autumn's gentle depth.",
        swapFor: "Mocha",
        swapHex: "#967969",
      },
      {
        name: "Cool Fuchsia",
        hex: "#FF00FF",
        reason: "This cool, electric pink clashes with your warm undertone and reads as costume-bright.",
        swapFor: "Dusty Plum",
        swapHex: "#9B6A6C",
      },
      {
        name: "Cool Navy",
        hex: "#000080",
        reason: "A cool, saturated navy is too sharp and blue-based for a soft warm palette.",
        swapFor: "Dusty Aqua",
        swapHex: "#88A0A8",
      },
      {
        name: "Vivid Orange",
        hex: "#FF6600",
        reason: "Full-saturation orange is brighter and clearer than anything in your muted range.",
        swapFor: "Terracotta",
        swapHex: "#E2725B",
      },
      {
        name: "Icy Lavender",
        hex: "#E6E6FA",
        reason: "Pale, icy purples read cool and washed-out rather than warm and blended.",
        swapFor: "Warm Taupe",
        swapHex: "#D2B1A3",
      },
    ],
    comparisons: [
      {
        vsLabel: "Soft Summer",
        vsHref: "/color-palette/summer/cool",
        rows: [
          { attribute: "Undertone", thisSeason: "Warm", otherSeason: "Cool" },
          { attribute: "Depth", thisSeason: "Light-medium", otherSeason: "Light-medium" },
          { attribute: "Chroma", thisSeason: "Muted, warm-based", otherSeason: "Muted, cool-based" },
          { attribute: "Best pink", thisSeason: "Dusty coral-pink", otherSeason: "Dusty rose-pink" },
          {
            attribute: "Metals",
            thisSeason: "Soft gold, rose gold",
            otherSeason: "Silver, soft rose gold",
            thisHex: "#D4B896",
            otherHex: "#C0C0C0",
          },
        ],
      },
      {
        vsLabel: "True Autumn",
        vsHref: "/color-palette/autumn/true",
        rows: [
          { attribute: "Undertone", thisSeason: "Warm, softened", otherSeason: "Warm, golden" },
          { attribute: "Chroma", thisSeason: "Muted, dusty", otherSeason: "Rich, saturated" },
          { attribute: "Contrast", thisSeason: "Low", otherSeason: "Medium" },
          {
            attribute: "Best red",
            thisSeason: "Soft rust",
            otherSeason: "Warm tomato red",
            thisHex: "#C87C5A",
            otherHex: "#E2483D",
          },
          {
            attribute: "Metals",
            thisSeason: "Soft gold",
            otherSeason: "Yellow gold, bronze",
            thisHex: "#D4B896",
            otherHex: "#D4A43A",
          },
        ],
      },
    ],
    capsule: [
      { item: "Wool coat", color: "Warm Taupe", hex: "#D2B1A3" },
      { item: "Blazer", color: "Mocha", hex: "#967969" },
      { item: "Blouse", color: "Wheat", hex: "#F5DEB3" },
      { item: "Trousers", color: "Soft Bronze", hex: "#B08D57" },
      { item: "Sweater", color: "Sage", hex: "#A3AA8B" },
      { item: "Dress", color: "Dusty Coral", hex: "#D8A48F" },
      { item: "Cardigan", color: "Moss Green", hex: "#8A9A5B" },
      { item: "Shoes", color: "Muted Camel", hex: "#C19A6B" },
      { item: "Bag", color: "Soft Olive", hex: "#A9A17A" },
      { item: "Denim", color: "Dusty Plum", hex: "#9B6A6C" },
    ],
    celebrities: [
      {
        name: "Jennifer Aniston",
        why: "Soft, blended golden-brown hair with muted warm skin and low overall contrast are a textbook Soft Autumn combination.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/1/16/JenniferAnistonHWoFFeb2012.jpg/330px-JenniferAnistonHWoFFeb2012.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Jennifer_Aniston",
      },
      {
        name: "Drew Barrymore",
        why: "Warm brown hair with a soft, muted undertone and gentle contrast against her skin give her the blended quality that defines Soft Autumn.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/5/52/Drew_Barrymore_in_2019_%28cropped%29.jpg/330px-Drew_Barrymore_in_2019_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Drew_Barrymore",
      },
      {
        name: "Jennifer Lawrence",
        why: "Warm, softly muted blonde-to-brown hair and low-to-medium contrast against her skin fit Soft Autumn's gentle, blended profile.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fe/Jennifer_Lawrence_in_2018.png/330px-Jennifer_Lawrence_in_2018.png",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Jennifer_Lawrence",
      },
      {
        name: "Gisele Bündchen",
        why: "Soft, sun-warmed blonde hair and muted golden skin with low-contrast, blended features are a natural fit for Soft Autumn.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/Ver%C3%A3o_Arezzo_Brasil_2019_por_Gisele_B%C3%BCndchen_17.jpg/330px-Ver%C3%A3o_Arezzo_Brasil_2019_por_Gisele_B%C3%BCndchen_17.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Gisele_B%C3%BCndchen",
      },
      {
        name: "Jessica Biel",
        why: "Warm, muted brown hair and softly blended skin tone with gentle overall contrast keep her in Soft Autumn's understated range.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/21/Jessica_Biel_2013.jpg/330px-Jessica_Biel_2013.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Jessica_Biel",
      },
    ],
    extraFaqs: [
      {
        question: "What's the difference between Soft Autumn and True Autumn?",
        answer:
          "Chroma. Soft Autumn colors are muted and dusty — like faded terracotta or sage. True Autumn colors are richer and more saturated — like pumpkin or rust. Both are warm; True Autumn just has more intensity and depth.",
      },
      {
        question: "What's the difference between Soft Autumn and Soft Summer?",
        answer:
          "Undertone. Both are muted, low-contrast palettes, but Soft Autumn is warm (olive, camel, warm taupe) and Soft Summer is cool (dusty blue, mauve, cool grey). Hold an olive and a dusty blue-grey up to your face — whichever looks more natural tells you which side you're on.",
      },
      {
        question: "What jewelry metals suit Soft Autumn?",
        answer: "Soft, matte gold and rose gold. Bright, polished yellow gold is too intense — look for brushed or antiqued finishes instead. Silver isn't wrong, but it tends to look flatter against warm skin than soft gold does.",
      },
      {
        question: "What hair colors work best for Soft Autumn?",
        answer:
          "Start with soft brown, bronde or muted auburn, then compare the depth and amount of warmth with your natural hair. Brighter copper, very cool ash and near-black create stronger contrasts with this muted palette. Use the same photo to compare and choose the effect you prefer; a season label does not require you to dye your hair.",
      },
      {
        question: "How do I confirm I'm Soft Autumn and not another season?",
        answer:
          "Drape yourself in a muted olive and a bright coral side by side. If the olive makes your skin look calm and even while the coral looks jarring, you're likely Soft Autumn. A professional or AI color analysis checking undertone, depth, and chroma together is the most reliable way to confirm.",
      },
    ],
    vibe: {
      skinTones: ["#F0DCC4", "#DEC19E", "#C2996E", "#A67A54"],
      hairTones: ["#8B7355", "#7A6B5B", "#8B5A45", "#A68B5B"],
      eyeColors: ["#8C7355", "#6B7A5A", "#9C9070", "#A8A0B0"],
    },
    hairToneSwatches: [
      { name: "Soft Brown", hexes: ["#5F4E38", "#8B7355", "#C9B698"] },
      { name: "Warm Ash Brown", hexes: ["#544A3F", "#7A6B5B", "#B8AB98"] },
      { name: "Muted Auburn", hexes: ["#5F3B2E", "#8B5A45", "#C99A7F"] },
      { name: "Soft Caramel", hexes: ["#6D5A38", "#A68B5B", "#E0CFA0"] },
      { name: "Muted Copper", hexes: ["#664731", "#9A6A4A", "#D8AC85"] },
      { name: "Soft Blonde", hexes: ["#8A7157", "#C4A484", "#EEDCC1"] },
      { name: "Warm Mushroom", hexes: ["#5F5346", "#8A7A6A", "#C9BCAC"] },
    ],
    metals: [
      { name: "Matte Gold", hexes: ["#A08359", "#D4B896", "#F0E1C6"] },
      { name: "Rose Gold", hexes: ["#977569", "#C9A090", "#EBD1C7"] },
      { name: "Antique Brass", hexes: ["#7C6947", "#A89060", "#D3BF94"] },
    ],
  },
  TRUE: {
    intro: `True Autumn — often called Warm Autumn — is the anchor of the autumn family: fully warm undertone, medium depth, and richer chroma than its Soft neighbor. Where Soft Autumn mutes everything down, True Autumn lets color run richer and more saturated, closer to peak foliage than early fall haze. Compared to True Spring, which shares the warm undertone, True Autumn is earthier and less clear — think pumpkin instead of tangerine, moss instead of grass green. Compared to Deep Autumn, the depth is more moderate; True Autumn doesn't need the darkest, most dramatic shades to look grounded. This is the true autumn color palette people picture first: goldenrod, rust, warm olive, copper, and terracotta, all with obvious gold undertone. Skin usually shows clear warmth — golden or peachy rather than neutral — and gold jewelry reads as noticeably more flattering than silver. If cool colors make you look sallow but rich, earthy tones make your whole face look lit from within, this is very likely your season.`,
    selfCheck: {
      skin: [
        "Clearly warm undertone — golden, peachy, or amber, never pink or ashy",
        "Looks healthy and glowing in rich earth tones like rust and pumpkin",
        "Tans easily and deeply; rarely burns without also browning",
        "Gold-based foundation and bronzer blend in seamlessly; pink-based ones look chalky",
      ],
      hair: [
        "Warm auburn, copper, chestnut, or golden brown — with visible warmth in the light",
        "Hair often lightens to coppery or golden tones in the sun rather than blonde",
        "Ashy, cool-toned dye jobs tend to look flat or brassy in an unflattering way",
        "Natural red or amber undertones are common even in brunette hair",
      ],
      eyes: [
        "Warm hazel, amber, golden brown, or warm green — rarely a cool blue or grey",
        "Eyes often have gold or copper flecks visible in daylight",
        "Warm eyeshadow (bronze, copper, olive) makes eyes look brighter and more open",
        "Cool-toned shadow (silver, icy blue) tends to look disconnected from the eye color",
      ],
      overall: [
        "Medium contrast — features are defined but not stark or high-drama",
        "Rich, saturated earth tones make you look vibrant rather than costumey",
        "Gold jewelry is unmistakably better than silver in side-by-side comparison",
        "Cool pastels and icy tones tend to wash you out or make skin look uneven",
      ],
    },
    avoidDetails: [
      {
        name: "Bright White",
        hex: "#FFFFFF",
        reason: "Stark white is too cool and flat next to True Autumn's warm, golden richness.",
        swapFor: "Golden Beige",
        swapHex: "#D8CAB8",
      },
      {
        name: "Pure Black",
        hex: "#000000",
        reason: "Pure black has no warmth and creates cold contrast against a golden undertone.",
        swapFor: "Sienna",
        swapHex: "#A0522D",
      },
      {
        name: "Cool Pink",
        hex: "#FF69B4",
        reason: "This blue-based pink fights your warm undertone and can make skin look ruddy.",
        swapFor: "Copper",
        swapHex: "#B87333",
      },
      {
        name: "Cool Navy",
        hex: "#000080",
        reason: "A cool, blue-heavy navy reads flat against warm skin — it needs a warm counterpart instead.",
        swapFor: "Warm Moss",
        swapHex: "#738678",
      },
      {
        name: "Icy Lavender",
        hex: "#E6E6FA",
        reason: "Pale cool purples have no gold in them and tend to wash out warm coloring.",
        swapFor: "Warm Saffron",
        swapHex: "#F4C430",
      },
      {
        name: "Silver-Grey",
        hex: "#C0C0C0",
        reason: "Cool, ashy grey lacks the warmth that makes True Autumn neutrals work.",
        swapFor: "Camel",
        swapHex: "#C19A6B",
      },
    ],
    comparisons: [
      {
        vsLabel: "Soft Autumn",
        vsHref: "/color-palette/autumn/soft",
        rows: [
          { attribute: "Undertone", thisSeason: "Warm, golden", otherSeason: "Warm, softened" },
          { attribute: "Chroma", thisSeason: "Rich, saturated", otherSeason: "Muted, dusty" },
          { attribute: "Contrast", thisSeason: "Medium", otherSeason: "Low" },
          {
            attribute: "Best red",
            thisSeason: "Warm tomato red",
            otherSeason: "Soft rust",
            thisHex: "#E2483D",
            otherHex: "#C87C5A",
          },
          {
            attribute: "Metals",
            thisSeason: "Yellow gold, bronze",
            otherSeason: "Soft gold",
            thisHex: "#D4A43A",
            otherHex: "#D4B896",
          },
        ],
      },
      {
        vsLabel: "Deep Autumn",
        vsHref: "/color-palette/autumn/deep",
        rows: [
          { attribute: "Depth", thisSeason: "Medium", otherSeason: "Deep" },
          { attribute: "Contrast", thisSeason: "Medium", otherSeason: "High" },
          { attribute: "Chroma", thisSeason: "Rich, warm", otherSeason: "Rich, warm, darker" },
          { attribute: "Best neutral", thisSeason: "Camel", otherSeason: "Espresso", thisHex: "#C19A6B", otherHex: "#3C2A21" },
          {
            attribute: "Metals",
            thisSeason: "Yellow gold",
            otherSeason: "Deep gold, antique brass",
            thisHex: "#D4A43A",
            otherHex: "#C4943A",
          },
        ],
      },
    ],
    capsule: [
      { item: "Wool coat", color: "Camel", hex: "#C19A6B" },
      { item: "Blazer", color: "Rust", hex: "#B7410E" },
      { item: "Shirt", color: "Warm Saffron", hex: "#F4C430" },
      { item: "Trousers", color: "Warm Taupe", hex: "#B49F8C" },
      { item: "Sweater", color: "Warm Olive", hex: "#6B8E23" },
      { item: "Dress", color: "Pumpkin", hex: "#FF7518" },
      { item: "Cardigan", color: "Bronze", hex: "#CD7F32" },
      { item: "Shoes", color: "Sienna", hex: "#A0522D" },
      { item: "Bag", color: "Copper", hex: "#B87333" },
      { item: "Denim", color: "Spice", hex: "#A0522D" },
    ],
    celebrities: [
      {
        name: "Julianne Moore",
        why: "Warm copper hair with golden, freckled skin is one of the clearest True Autumn combinations in the public eye.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8f/Julianne_Moore_at_the_2026_Cannes_Film_Festival-65472_%28cropped%29.jpg/330px-Julianne_Moore_at_the_2026_Cannes_Film_Festival-65472_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Julianne_Moore",
      },
      {
        name: "Debra Messing",
        why: "Rich auburn hair and warm, golden skin undertone are textbook True Autumn richness.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2b/Debra_Messing_at_the_2009_Tribeca_Film_Festival.jpg/330px-Debra_Messing_at_the_2009_Tribeca_Film_Festival.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Debra_Messing",
      },
      {
        name: "Eva Mendes",
        why: "Warm, golden-olive skin with medium-depth warm hair shows the saturated warmth of this season.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/11/Eva_Mendes_2009.jpg/330px-Eva_Mendes_2009.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Eva_Mendes",
      },
      {
        name: "Julia Roberts",
        why: "Warm reddish-brown hair and golden undertone with medium contrast are classic True Autumn markers.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d3/Julia_Roberts_2025.jpg/330px-Julia_Roberts_2025.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Julia_Roberts",
      },
      {
        name: "Christina Hendricks",
        why: "Vivid copper-red hair with warm, golden skin is one of the most cited True Autumn examples.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/2/26/Christina_Hendricks_at_PaleyFest_2014.jpg/330px-Christina_Hendricks_at_PaleyFest_2014.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Christina_Hendricks",
      },
    ],
    extraFaqs: [
      {
        question: "What's the difference between True Autumn and Soft Autumn?",
        answer:
          "Saturation. True Autumn wears rich, fully saturated earth tones like pumpkin and rust. Soft Autumn wears the same color family muted down — dusty terracotta instead of pumpkin. Both are warm; True Autumn just has more color intensity.",
      },
      {
        question: "What's the difference between True Autumn and Deep Autumn?",
        answer:
          "Depth. True Autumn sits at medium depth with medium contrast — rich but not the darkest option. Deep Autumn goes darker and more dramatic, often with near-black hair against warm skin. If camel and rust feel like your ceiling, you're True; if espresso and deep burgundy feel natural, you're Deep.",
      },
      {
        question: "Can True Autumn wear black?",
        answer:
          "It's usually your hardest neutral. Black has no warmth and can look severe against golden skin. Deep brown, espresso, or dark olive give you the same grounding depth with warmth intact — try those before reaching for black.",
      },
      {
        question: "What jewelry metals suit True Autumn?",
        answer: "Yellow gold, brass, copper, and bronze. These warm metals match your golden undertone directly. Silver and platinum tend to look cool and slightly flat against True Autumn skin — save them for a cooler-toned outfit if you wear them at all.",
      },
      {
        question: "How do I confirm I'm True Autumn?",
        answer:
          "Compare a warm tomato red to a blue-based cherry red under natural light. If the tomato red makes your skin look even and glowing while the cherry red looks harsh, that's a strong warm-undertone signal. Pair that with medium (not dark) natural contrast, and True Autumn is likely — a full color analysis confirms depth and chroma together.",
      },
    ],
    vibe: {
      skinTones: ["#EFD3AC", "#D9AD74", "#B9824C", "#8F5E34"],
      hairTones: ["#8B4513", "#8B5A2B", "#7B4B2A", "#8A6A3A"],
      eyeColors: ["#6B4A2C", "#8A5A2A", "#5A6B2A", "#3A2A1A"],
    },
    hairToneSwatches: [
      { name: "Warm Auburn", hexes: ["#5E300D", "#8B4513", "#C97D42"] },
      { name: "Copper Brown", hexes: ["#5E3D1D", "#8B5A2B", "#C99459"] },
      { name: "Chestnut", hexes: ["#52321C", "#7B4B2A", "#B98559"] },
      { name: "Warm Golden Brown", hexes: ["#5C4A26", "#8A6A3A", "#C7A96C"] },
      { name: "Bright Copper", hexes: ["#7C5525", "#B87333", "#E8A868"] },
      { name: "Rich Ginger", hexes: ["#66311C", "#9A4A2A", "#D07E56"] },
      { name: "Warm Black", hexes: ["#0E0B08", "#2C2416", "#4C4030"] },
    ],
    metals: [
      { name: "Yellow Gold", hexes: ["#9F7A26", "#D4A43A", "#F0D080"] },
      { name: "Copper", hexes: ["#7C5525", "#B87333", "#E8A868"] },
      { name: "Brass", hexes: ["#7C763A", "#B5A642", "#E0D584"] },
    ],
  },
  DEEP: {
    intro: `Deep Autumn is the darkest, most dramatic member of the autumn family — full warm undertone carried at real depth. Compared to True Autumn, everything sits several shades darker: hair is usually dark brown to near-black with warmth underneath, and skin can range from deep olive to rich espresso, always with gold rather than blue in it. Chroma stays rich rather than muted, so this isn't a soft palette — it's saturated color pushed into darker, more intense territory, like burnished leather or deep forest green rather than pastel fall. The closest confusable season is Deep Winter, and the split comes down to undertone alone: Deep Autumn needs a warm base under every dark color (chocolate, burnt orange, olive), while Deep Winter needs a cool one (true black, icy jewel tones). If pastels and light neutrals leave you looking flat and colorless, but deep, rich, warm shades make your features pop and your skin look luminous, this deep autumn color palette is built for you.`,
    selfCheck: {
      skin: [
        "Warm undertone at medium-to-deep depth — olive, caramel, deep golden, or rich brown",
        "Looks flat or ashen in pastels; comes alive in deep, saturated color",
        "Tans very easily, often already reads as tan or deep year-round",
        "Gold and bronze makeup tones blend in; cool pink tones look chalky",
      ],
      hair: [
        "Dark brown to near-black, but with visible warmth (auburn or chestnut cast) in sunlight",
        "Blue-black or ash-toned dye jobs tend to look harsh rather than sleek",
        "High-contrast against lighter skin, or naturally deep alongside deep skin",
        "Warm balayage or copper highlights blend more naturally than cool or platinum ones",
      ],
      eyes: [
        "Deep brown, warm hazel, or dark amber — rich rather than pale",
        "Eyes often show real depth and intensity, holding attention without added makeup",
        "Bronze, chocolate, and deep olive shadow make eyes look even richer",
        "Pale, icy, or cool-toned shadow tends to look chalky against deep warm coloring",
      ],
      overall: [
        "High natural contrast — features read as bold and defined, not soft",
        "Deep, rich colors (burgundy, chocolate, forest green) look powerful, not overwhelming",
        "Gold jewelry reads noticeably richer and more flattering than silver",
        "Light, pastel, or icy colors make the whole look feel washed out and undefined",
      ],
    },
    avoidDetails: [
      {
        name: "Icy Pink",
        hex: "#FFDEE9",
        reason: "This pale, cool pink is too light and undertone-mismatched to register against deep warm coloring.",
        swapFor: "Deep Terracotta",
        swapHex: "#9B4722",
      },
      {
        name: "Pure White",
        hex: "#FFFFFF",
        reason: "Stark white creates cold, flat contrast instead of the rich depth this palette calls for.",
        swapFor: "Deep Camel",
        swapHex: "#A67B5B",
      },
      {
        name: "Cool Blue",
        hex: "#00BFFF",
        reason: "A bright, cool blue has no warmth and clashes with a golden-to-olive undertone.",
        swapFor: "Deep Teal",
        swapHex: "#006666",
      },
      {
        name: "Pastel Lilac",
        hex: "#C8A2C8",
        reason: "Pale, cool lilac is both too light and too cool for Deep Autumn's rich, warm intensity.",
        swapFor: "Maroon",
        swapHex: "#800000",
      },
      {
        name: "Cool Grey",
        hex: "#A9A9A9",
        reason: "Flat, blue-based grey has no warmth and reads dull against deep golden or olive skin.",
        swapFor: "Dark Bronze",
        swapHex: "#645452",
      },
      {
        name: "Bright Lemon Yellow",
        hex: "#FFF44F",
        reason: "This is too light and clear for Deep Autumn's saturated, darker chroma.",
        swapFor: "Dark Amber",
        swapHex: "#996515",
      },
    ],
    comparisons: [
      {
        vsLabel: "True Autumn",
        vsHref: "/color-palette/autumn/true",
        rows: [
          { attribute: "Depth", thisSeason: "Deep", otherSeason: "Medium" },
          { attribute: "Contrast", thisSeason: "High", otherSeason: "Medium" },
          { attribute: "Chroma", thisSeason: "Rich, warm, darker", otherSeason: "Rich, warm" },
          { attribute: "Best neutral", thisSeason: "Espresso", otherSeason: "Camel", thisHex: "#3C2A21", otherHex: "#C19A6B" },
          {
            attribute: "Metals",
            thisSeason: "Deep gold, antique brass",
            otherSeason: "Yellow gold",
            thisHex: "#C4943A",
            otherHex: "#D4A43A",
          },
        ],
      },
      {
        vsLabel: "Deep Winter",
        vsHref: "/color-palette/winter/deep",
        rows: [
          { attribute: "Undertone", thisSeason: "Warm", otherSeason: "Cool" },
          { attribute: "Depth", thisSeason: "Deep", otherSeason: "Deep" },
          { attribute: "Best burgundy", thisSeason: "Warm, brown-based", otherSeason: "Cool, blue-based" },
          {
            attribute: "Best neutral",
            thisSeason: "Chocolate brown",
            otherSeason: "True black",
            thisHex: "#381819",
            otherHex: "#000000",
          },
          {
            attribute: "Metals",
            thisSeason: "Deep gold",
            otherSeason: "Silver, platinum",
            thisHex: "#C4943A",
            otherHex: "#C0C0C0",
          },
        ],
      },
    ],
    capsule: [
      { item: "Wool coat", color: "Dark Chestnut", hex: "#5C3317" },
      { item: "Blazer", color: "Deep Mahogany", hex: "#4A1C17" },
      { item: "Shirt", color: "Dark Amber", hex: "#996515" },
      { item: "Trousers", color: "Espresso", hex: "#3C2A21" },
      { item: "Sweater", color: "Deep Olive", hex: "#556B2F" },
      { item: "Dress", color: "Burnt Umber", hex: "#8A3324" },
      { item: "Cardigan", color: "Deep Rust", hex: "#8B2500" },
      { item: "Shoes", color: "Dark Chocolate", hex: "#381819" },
      { item: "Bag", color: "Walnut", hex: "#773F1A" },
      { item: "Denim", color: "Maroon", hex: "#800000" },
    ],
    celebrities: [
      {
        name: "Sofia Vergara",
        why: "Rich, deep brunette hair with warm golden skin and strong natural contrast show the intensity that defines Deep Autumn.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/1/14/Sof%C3%ADa_Vergara_2019_by_Glenn_Francis.jpg/330px-Sof%C3%ADa_Vergara_2019_by_Glenn_Francis.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Sof%C3%ADa_Vergara",
      },
      {
        name: "Salma Hayek",
        why: "Deep, richly warm brown hair and high-contrast features against warm skin show real Deep Autumn intensity.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/8/80/MKr383631_Salma_Hayek_%28Women_In_Motion%2C_Cannes_2025%29_crop.jpg/330px-MKr383631_Salma_Hayek_%28Women_In_Motion%2C_Cannes_2025%29_crop.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Salma_Hayek",
      },
      {
        name: "Penélope Cruz",
        why: "Warm olive-toned skin and deep, richly dark hair with strong natural contrast are classic Deep Autumn markers.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ad/Goyas_2024_-_Pen%C3%A9lope_Cruz-2_%28cropped%29.jpg/330px-Goyas_2024_-_Pen%C3%A9lope_Cruz-2_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Pen%C3%A9lope_Cruz",
      },
      {
        name: "Eva Longoria",
        why: "Deep warm skin and richly dark hair color exemplify Deep Autumn's dense, dramatic warmth.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f8/Eva_Longoria-1384.jpg/330px-Eva_Longoria-1384.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Eva_Longoria",
      },
      {
        name: "Jessica Alba",
        why: "Rich warm undertones and deep, high-contrast coloring against dark hair keep her clearly in the intense Deep Autumn range.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/8/81/Jessica_Alba_-_Los_Angeles_Comic_Con_2025.jpg/330px-Jessica_Alba_-_Los_Angeles_Comic_Con_2025.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Jessica_Alba",
      },
      {
        name: "Rachel Weisz",
        why: "Deep, rich brown hair and warm skin tone with strong overall contrast fit the Deep Autumn profile well.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/2/22/Rachel_Weisz-62940_%28cropped%29.jpg/330px-Rachel_Weisz-62940_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Rachel_Weisz",
      },
      {
        name: "Viola Davis",
        why: "Rich, deep warm undertones and dense, high-contrast depth are why she's frequently cited as Deep Autumn.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/b/bb/Viola_Davis_at_the_Air_Premiere_at_SXSW_%28cropped%29.jpg/330px-Viola_Davis_at_the_Air_Premiere_at_SXSW_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Viola_Davis",
      },
    ],
    extraFaqs: [
      {
        question: "Can Deep Autumn wear black?",
        answer:
          "Better than other Autumns, but warm darks still work harder for you. Deep chocolate brown, espresso, or warm charcoal give the same grounding depth as black while staying connected to your golden undertone. Save pure black for accents rather than head-to-toe.",
      },
      {
        question: "What's the difference between Deep Autumn and True Autumn?",
        answer:
          "Depth and contrast. True Autumn sits at medium depth with medium contrast — rich but not dark. Deep Autumn goes darker with higher contrast, often near-black hair against warm skin. If espresso and deep burgundy feel more like 'you' than camel and rust, you're Deep.",
      },
      {
        question: "What's the difference between Deep Autumn and Deep Winter?",
        answer:
          "Undertone. Both wear deep, dramatic color well, but Deep Autumn needs warmth underneath — warm burgundy, chocolate, olive — while Deep Winter needs cool — blue-based burgundy, true black, pine. Hold a warm and cool burgundy side by side; whichever makes your skin look clearer is your answer.",
      },
      {
        question: "What jewelry metals suit Deep Autumn?",
        answer: "Deep or antiqued gold, brass, and bronze. These have enough richness to match your depth without looking thin. Bright, polished yellow gold can look slightly light for you — an oxidized or antique finish usually reads better.",
      },
      {
        question: "What hair colors work for Deep Autumn?",
        answer:
          "Dark chocolate, deep auburn, rich mahogany, or espresso — all with warmth underneath. Avoid blue-black or ash-toned dye, which fights your golden undertone and can make skin look sallow instead of rich.",
      },
      {
        question: "How do I confirm I'm Deep Autumn?",
        answer:
          "Try a rich, warm burgundy next to a cool, blue-based one. If the warm version makes your skin glow while the cool one looks slightly off, and you also read as naturally high-contrast and deep, Deep Autumn is likely. A full color analysis checking undertone, depth, and contrast together gives the most reliable confirmation.",
      },
    ],
    vibe: {
      skinTones: ["#D9AD78", "#B9824C", "#8F5E34", "#5C3A20"],
      hairTones: ["#3C2415", "#5A2A1A", "#2A1A10", "#4A3020"],
      eyeColors: ["#3D2817", "#6B4A2C", "#4A5A3A", "#1A1008"],
    },
    hairToneSwatches: [
      { name: "Dark Chocolate", hexes: ["#180F08", "#3C2415", "#7A5232"] },
      { name: "Deep Auburn", hexes: ["#2E1408", "#5A2A1A", "#9A5738"] },
      { name: "Rich Espresso", hexes: ["#150D06", "#2A1A10", "#5C4025"] },
      { name: "Dark Warm Brown", hexes: ["#241810", "#4A3020", "#8A6242"] },
      { name: "Warm Black", hexes: ["#0A0806", "#1C1410", "#3C3226"] },
      { name: "Deep Chestnut", hexes: ["#2E1E15", "#5A3A2A", "#9A7255"] },
      { name: "Rich Mahogany", hexes: ["#2E1510", "#5A2A20", "#9A5A48"] },
    ],
    metals: [
      { name: "Rich Gold", hexes: ["#93701D", "#C4943A", "#E8C87E"] },
      { name: "Antique Brass", hexes: ["#4C3B1D", "#8A6A3A", "#BC9C68"] },
      { name: "Bronze", hexes: ["#4C3B1D", "#8A5A3A", "#BC8A68"] },
    ],
  },
};
